import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Clock,
  CheckCircle,
  XCircle,
  BedDouble,
  User,
  Phone,
  CreditCard,
  MapPin,
  RefreshCw,
  Home,
  IndianRupee,
  Shield,
  Loader2,
  AlertCircle,
  ShieldCheck
} from 'lucide-react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';
import { bookingsAPI, paymentAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const BookingStatus = () => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  // True when the API returned the privacy-masked view (caller is not the booking's
  // owner and not an admin for its station).
  const [isRedacted, setIsRedacted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [initiatingPayment, setInitiatingPayment] = useState(false);
  const hasVerified = useRef(false);

  useEffect(() => {
    hasVerified.current = false;
    fetchBookingStatus();
  }, [id]);

  // Auto-verify payment when user returns from PhonePe redirect
  useEffect(() => {
    const orderId = booking?.merchantOrderId || booking?.transactionId;
    if (booking && orderId && booking.paymentStatus !== 'COMPLETED' && !hasVerified.current) {
      hasVerified.current = true;
      verifyPaymentStatus(orderId);
    }
  }, [booking]);

  const fetchBookingStatus = async () => {
    setLoading(true);
    try {
      const response = await bookingsAPI.getStatus(id);
      setBooking(response.data.booking);
      setIsRedacted(Boolean(response.data.redacted));
      setError(null);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Booking not found');
    } finally {
      setLoading(false);
    }
  };

  const verifyPaymentStatus = async (transactionId) => {
    setVerifyingPayment(true);
    try {
      const response = await paymentAPI.checkStatus(transactionId);
      if (response.data.status === 'COMPLETED') {
        toast.success('Payment verified successfully!');
        // Refresh booking to get updated status
        await fetchBookingStatus();
      } else if (response.data.status === 'FAILED') {
        toast.error('Payment failed. You can retry the payment below.');
        await fetchBookingStatus();
      }
      // If PENDING, PhonePe callback will update eventually
    } catch (err) {
      console.error('Payment verification error:', err);
    } finally {
      setVerifyingPayment(false);
    }
  };

  const handlePayment = async () => {
    setInitiatingPayment(true);
    try {
      const response = await paymentAPI.initiate(booking.id);
      if (response.data.success && response.data.redirectUrl) {
        window.location.href = response.data.redirectUrl;
        return;
      } else {
        toast.error('Failed to initiate payment');
      }
    } catch (err) {
      console.error('Payment Error:', err);
      toast.error(err.response?.data?.error || 'Payment initiation failed');
    } finally {
      setInitiatingPayment(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      payment_pending: {
        icon: CreditCard,
        color: 'orange',
        text: 'Payment Pending',
        description: 'Complete your payment to confirm the booking'
      },
      pending: {
        icon: Clock,
        color: 'yellow',
        text: 'Pending Approval',
        description: 'Payment received! Your request is being reviewed by the station admin.'
      },
      approved: {
        icon: CheckCircle,
        color: 'green',
        text: 'Approved',
        description: 'Room has been allocated! Please visit the station for check-in.'
      },
      checked_in: {
        icon: BedDouble,
        color: 'blue',
        text: 'Checked In',
        description: 'You are currently checked in. Enjoy your stay!'
      },
      checked_out: {
        icon: CheckCircle,
        color: 'midnight',
        text: 'Checked Out',
        description: 'Thank you for staying with us!'
      },
      rejected: {
        icon: XCircle,
        color: 'red',
        text: 'Rejected',
        description: 'Unfortunately, your request could not be fulfilled.'
      },
      cancelled: {
        icon: XCircle,
        color: 'midnight',
        text: 'Cancelled',
        description: 'This booking has been cancelled.'
      }
    };
    return configs[status] || configs.pending;
  };

  const colorClasses = {
    orange: 'bg-orange-100 text-orange-600 border-orange-200',
    yellow: 'bg-yellow-100 text-yellow-600 border-yellow-200',
    green: 'bg-green-100 text-green-600 border-green-200',
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    red: 'bg-red-100 text-red-600 border-red-200',
    midnight: 'bg-midnight-100 text-midnight-600 border-midnight-200',
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center py-16">
          <XCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-2xl font-display font-bold text-midnight-900 mb-2">
            Booking Not Found
          </h2>
          <p className="text-midnight-600 mb-6">
            The booking reference you provided doesn't exist
          </p>
          <Link to="/" className="btn-primary">
            Go to Home
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const statusConfig = getStatusConfig(booking.status);
  const StatusIcon = statusConfig.icon;
  const isPaymentPending = booking.status === 'payment_pending';
  const isPaymentFailed = booking.paymentStatus === 'FAILED';
  const isPaymentCompleted = booking.paymentStatus === 'COMPLETED';

  return (
    <PublicLayout>
      <SEO
        title="Booking Status"
        description="Check the status of your retiring room booking. View allocation details, payment status, and check-in information."
        noIndex
      />
      <div className="min-h-[60vh] bg-gradient-to-b from-midnight-50 to-white py-12">
        <div className="max-w-2xl mx-auto px-4">

          {/* Verifying Payment Banner */}
          {verifyingPayment && (
            <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200 flex items-center gap-3 animate-pulse">
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              <span className="text-blue-700 font-medium">Verifying your payment...</span>
            </div>
          )}

          {/* Status Header */}
          <div className="card mb-6 animate-slide-up">
            <div className={`p-8 text-center border-b-4 ${colorClasses[statusConfig.color]}`}>
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${colorClasses[statusConfig.color]}`}>
                <StatusIcon className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-display font-bold text-midnight-900">
                {statusConfig.text}
              </h2>
              <p className="text-midnight-600 mt-2">
                {statusConfig.description}
              </p>
            </div>

            <div className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-midnight-500">Booking Reference</span>
                <span className="font-mono font-bold text-midnight-900">{booking.id}</span>
              </div>

              {/* Payment Status Indicator */}
              {isPaymentCompleted && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-green-800 font-medium text-sm">Payment Completed</span>
                  {booking.totalAmount > 0 && (
                    <span className="ml-auto font-bold text-green-700 flex items-center gap-0.5">
                      <IndianRupee className="w-3.5 h-3.5" />
                      {booking.totalAmount}
                    </span>
                  )}
                </div>
              )}

              {isPaymentFailed && (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <span className="text-red-800 font-medium text-sm">Payment Failed</span>
                </div>
              )}

              {/* Pay Now Button - shown for payment_pending or failed */}
              {(isPaymentPending || isPaymentFailed) && (
                <button
                  onClick={handlePayment}
                  disabled={initiatingPayment}
                  className="w-full flex items-center justify-center gap-3 py-4 px-6 text-lg font-bold text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg hover:from-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {initiatingPayment ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Redirecting to Payment...</span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5" />
                      <span>
                        {isPaymentFailed ? 'Retry Payment' : 'Pay Now'}
                        {booking.totalAmount > 0 ? ` - ₹${booking.totalAmount}` : ''}
                      </span>
                    </>
                  )}
                </button>
              )}

              {(isPaymentPending || isPaymentFailed) && (
                <p className="text-center text-xs text-midnight-400">
                  Secure payment via PhonePe. Your booking will be sent to admin after payment.
                </p>
              )}

              <button
                onClick={fetchBookingStatus}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Status
              </button>
            </div>
          </div>

          {/* Booking Details */}
          <div className="card animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <div className="card-header">
              <h3 className="text-lg font-display font-semibold">Booking Details</h3>
            </div>

            <div className="p-6 space-y-4">
              {/* Guest details are masked for anyone who is not signed in as the guest (or
                  as an admin for the station) — a booking link alone must not expose a
                  passenger's PNR and phone number to whoever holds it. */}
              {isRedacted && (
                <div className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm">
                  <ShieldCheck className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span className="text-blue-900">
                    Guest details are partly hidden for privacy.{' '}
                    <Link to="/user/login" className="font-semibold underline">Sign in</Link>{' '}
                    with the account that made this booking to see them in full.
                  </span>
                </div>
              )}

              {/* Guest Info */}
              <div className="flex items-center gap-4 p-4 bg-midnight-50 rounded-xl">
                <User className="w-10 h-10 text-midnight-400" />
                <div>
                  <p className="font-semibold text-midnight-900">{booking.guestName}</p>
                  <p className="text-sm text-midnight-500">Guest Name</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-midnight-50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-midnight-400" />
                  <div>
                    <p className="text-xs text-midnight-500">PNR</p>
                    <p className="font-medium text-midnight-900">{booking.pnr || booking.prn || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-midnight-50 rounded-lg">
                  <Phone className="w-5 h-5 text-midnight-400" />
                  <div>
                    <p className="text-xs text-midnight-500">Mobile</p>
                    <p className="font-medium text-midnight-900">
                      {booking.mobile ? `${booking.countryCode || '+91'} ${booking.mobile}` : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Station */}
              <div className="flex items-center gap-4 p-4 bg-railway-50 rounded-xl border border-railway-100">
                <MapPin className="w-10 h-10 text-railway-500" />
                <div>
                  <p className="font-semibold text-midnight-900">
                    {booking.station?.name}
                  </p>
                  <p className="text-sm text-midnight-500">
                    Station Code: {booking.station?.code}
                  </p>
                </div>
              </div>

              {/* Scheduled Date/Time */}
              {booking.scheduledDate && (
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Scheduled Check-in
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-blue-600">Date</p>
                      <p className="font-bold text-blue-900">
                        {new Date(booking.scheduledDate).toLocaleDateString('en-IN', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    {booking.scheduledTimeSlot && (
                      <div>
                        <p className="text-xs text-blue-600">Time</p>
                        <p className="font-bold text-blue-900">{booking.scheduledTimeSlot}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Payment Details */}
              {booking.totalAmount > 0 && (
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <IndianRupee className="w-4 h-4" />
                    Payment Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    {booking.price > 0 && (
                      <div className="flex justify-between">
                        <span className="text-purple-600">Room Charges</span>
                        <span className="font-medium text-purple-900 flex items-center gap-0.5">
                          <IndianRupee className="w-3 h-3" />
                          {booking.price}
                        </span>
                      </div>
                    )}
                    {booking.gstPercentage > 0 && (
                      <div className="flex justify-between">
                        <span className="text-purple-600">GST ({booking.gstPercentage}%)</span>
                        <span className="font-medium text-purple-900 flex items-center gap-0.5">
                          <IndianRupee className="w-3 h-3" />
                          {booking.gstAmount}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-base border-t border-purple-200 pt-2 mt-2">
                      <span className="text-purple-900">Total</span>
                      <span className="text-purple-900 flex items-center gap-0.5">
                        <IndianRupee className="w-3.5 h-3.5" />
                        {booking.totalAmount}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-purple-500">Payment Status</span>
                      <span className={`font-medium ${booking.paymentStatus === 'COMPLETED' ? 'text-green-600' :
                        booking.paymentStatus === 'FAILED' ? 'text-red-600' :
                          'text-orange-600'
                        }`}>
                        {booking.paymentStatus || 'PENDING'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Allocated Room (if approved) */}
              {booking.allocatedRoom && (
                <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                  <h4 className="font-semibold text-green-800 mb-3">Allocated Room</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-green-600">Room Number</p>
                      <p className="font-bold text-green-900">{booking.allocatedRoom.roomNumber}</p>
                    </div>
                    <div>
                      <p className="text-xs text-green-600">Floor</p>
                      <p className="font-bold text-green-900">{booking.allocatedRoom.floor || 'Ground'}</p>
                    </div>
                    {booking.allocatedCategory && (
                      <div className="col-span-2">
                        <p className="text-xs text-green-600">Category</p>
                        <p className="font-bold text-green-900">{booking.allocatedCategory.name}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="border-t border-midnight-100 pt-4 mt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-midnight-500">Requested At</p>
                    <p className="font-medium text-midnight-900">
                      {new Date(booking.requestedAt).toLocaleString('en-IN')}
                    </p>
                  </div>
                  {booking.allocatedAt && (
                    <div>
                      <p className="text-midnight-500">Allocated At</p>
                      <p className="font-medium text-midnight-900">
                        {new Date(booking.allocatedAt).toLocaleString('en-IN')}
                      </p>
                    </div>
                  )}
                  {booking.checkInAt && (
                    <div>
                      <p className="text-midnight-500">Check-in At</p>
                      <p className="font-medium text-midnight-900">
                        {new Date(booking.checkInAt).toLocaleString('en-IN')}
                      </p>
                    </div>
                  )}
                  {booking.checkOutAt && (
                    <div>
                      <p className="text-midnight-500">Check-out At</p>
                      <p className="font-medium text-midnight-900">
                        {new Date(booking.checkOutAt).toLocaleString('en-IN')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-railway-600 hover:text-railway-700 font-medium">
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default BookingStatus;
